import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ConfigurationFilter, ProductConfiguration } from 'src/app/models/fitting-query-params.model';
import { LookupValue, lookupValueFactory } from '../../models/lookup.model';

import * as fromReducer from '../reducers/fittings.reducer';

const state = createFeatureSelector<fromReducer.State>(fromReducer.fittingsFeatureKey);

export const selectPipelineTypes = createSelector(state, state => state.pipelineTypes);
export const selectAllPipeSizes = createSelector(state, state => (state || {}).pipeSizes);
export const selectFittingTypes = createSelector(state, state => (state || {}).fittingTypes);
export const selectRegions = createSelector(state, state => (state || {}).regions);
export const selectAnsiClasses = createSelector(state, state => (state || {}).ansiClasses);
export const selectFlangeTypes = createSelector(state, state => (state || {}).flangeTypes);
export const selectDesignCodes = createSelector(state, state => (state || {}).designCodes);
export const selectDesignFactors = createSelector(state, state => [...(state || {}).designFactors, lookupValueFactory({ text: '', numericValue: -1, id: -1 })]);
export const selectMaxCes = createSelector(state, state => (state || {}).maxCes);

export const selectHasLoadedLookups = createSelector(selectFittingTypes, selectRegions, selectAnsiClasses, selectFlangeTypes, selectDesignCodes, selectDesignFactors, selectMaxCes,
  (fittingTypes, regions, ansiClasses, flangeTypes, designCodes, designFactors, maxCes) => (fittingTypes.length > 0 || regions.length > 0 || ansiClasses.length > 0 || flangeTypes.length > 0 || designCodes.length > 0 || maxCes.length > 0));

export const selectQuery = createSelector(state, state => state?.query);
export const selectFittingFilterIsDirty = createSelector(selectQuery, q => q?.isDirty);

export const selectPipelineType = createSelector(state, state => state.pipelineType);
export const selectDesignCode = createSelector(state, state => state.query?.designCodeId || -1);

export const selectPipeSizes = createSelector(selectAllPipeSizes, selectPipelineType,
  (sizes, pipeType) => pipeType === 1 ? sizes.filter(f => f.text?.includes('Special')) : sizes.filter(f => !f.text?.includes('Special')));

// the branch sizes should be <= the selected pipeline size. If special pipeline type, we add 1 to the selected pipeline size.
export const selectBranchSizes = createSelector(selectPipeSizes, selectQuery, selectPipelineType, (pipeSizes, query, pipeType) => {
  const selectedPipeSizeId = (query?.pipeSizeId || -1) + (pipeType || 0);
  const selectedPipeSizeNumeric = pipeSizes.find(i => i.id === selectedPipeSizeId)?.numericValue;
  const res: LookupValue[] = pipeSizes.filter(sizes => (sizes.numericValue || 100) <= (selectedPipeSizeNumeric || -1));
  return pipeType === 0 ? res.filter(f => !f.text?.includes('Special')) : res.filter(f => f.text?.includes('Special'));
});

export const selectActiveFittingTypeId = createSelector(selectQuery, q => q?.fittingTypeId);
export const selectActiveFittingType = createSelector(selectActiveFittingTypeId, selectFittingTypes, (id, types) => {
  if (!!id) return types.find(f => f.id === id);
  return null;
});

export const selectQueryResult = createSelector(state, state => state?.filterResult);
export const selectActiveFittingId = createSelector(state, state => state?.activeFittingId);
export const selectActiveFitting = createSelector(selectQueryResult, selectActiveFittingId,
  (result, id) => (result || []).find(f => f.id === id));
export const selectActiveFittingPartNumber = createSelector(state, state => state.activeFittingPartnumber);
export const selectActiveFittingHasDrawing = createSelector(selectActiveFitting, fitting => fitting?.hasDrawing);


export const selectActiveFittingDetail = createSelector(state, state => state?.activeFittingDetail);
export const selectIncompatibleProducts = createSelector(state, state => state.lowTempFkmOringsCommonIncompatibleProducts);
export const selectAdditionalDwgVisibilityForActiveFitting = createSelector(state, state => state.activeFittingDrawings);
export const selectAdditionalDwgForActiveFitting = createSelector(selectAdditionalDwgVisibilityForActiveFitting, dwgs => {
  const res = [];
  for (const k in dwgs) {
    if (Object.prototype.hasOwnProperty.call(dwgs, k)) {
      const element = dwgs[k];
      if (!element.error) res.push(k);
    }
  }

  return res;
});

export const selectConfigurationFilterLookups = createSelector(state, state => state.configurationFiltersLookup || { tappedHoleSizes: [], bevelOptions: [], oringOptions: [] });
export const selectConfigurationFilterLookupsTappedHoles = createSelector(selectConfigurationFilterLookups, lookups => lookups.tappedHoleSizes);
export const selectConfigurationFilterLookupsOrings = createSelector(selectConfigurationFilterLookups, lookups => lookups.oringOptions);
export const selectConfigurationFilterLookupsBevels = createSelector(selectConfigurationFilterLookups, lookups => lookups.bevelOptions);

// Top Level Configurations
export const selectConfigurationQuery = createSelector(state, state => state?.configurationsFilter);
export const selectActiveFittingConfigurations = createSelector(state, state => state?.activeFittingConfigurations || []);

export const selectTappedHolesFilterValue = createSelector(selectConfigurationQuery, q => q?.tappedBlind);
export const selectTappedHolesFilterSplit = createSelector(selectConfigurationQuery, q => (q?.tappedBlind || '').split(',').filter(f => f.length > 0).map(v => v.trim()));

export const selectOringFilterValue = createSelector(selectConfigurationQuery, q => q?.oring);
export const selectOringFilterSplit = createSelector(selectConfigurationQuery, q => (q?.oring || '').split(',').filter(f => f.length > 0).map(v => v.trim()));

export const selectBevelFilterValue = createSelector(selectConfigurationQuery, q => q?.bevel);
export const selectBevelFilterSplit = createSelector(selectConfigurationQuery, q => (q?.bevel || '').split(',').filter(f => f.length > 0).map(v => v.trim()));

// this logic is used to filter configurations at the top leve and at the product configurations
const filterConfigurations = (configs: ProductConfiguration[], q?: ConfigurationFilter, tappedHoles?: string[], orings?: string[], bevels?: string[]) => {
  return configs
    .filter(v => !!q?.buyAmerica ? v.availableConfiguration?.description.includes('BA') : true)
    .filter(v => !!q?.magInspection ? v.availableConfiguration?.description.toLowerCase().includes('mag') : true)
    .filter(v => !!q?.liftingLugs ? v.availableConfiguration?.description.toLowerCase().includes('lifting lugs') : true)

    .filter(v => !!q?.tappedBlind && q.tappedBlind.length > 0 ? (tappedHoles || []).filter(h => v.availableConfiguration?.description.includes(h)).length > 0 : true)
    .filter(v => !!q?.oring && q.oring.length > 0 ? (orings || []).filter(h => v.availableConfiguration?.description.includes(h)).length > 0 : true)
    .filter(v => !!q?.bevel && q.bevel.length > 0 ? (bevels || []).filter(h => v.availableConfiguration?.description.includes(h)).length > 0 : true)
};

export const selectActiveFittingConfigurationsHasFilter = createSelector(selectConfigurationQuery,
  q => !(!q?.buyAmerica && !q?.magInspection && !q?.liftingLugs &&
    (q?.tappedBlind || '').length === 0 && (q?.oring || '').length === 0 && (q?.bevel || '').length === 0));
export const selectActiveFittingConfigurationsWithFilter = createSelector(
  selectActiveFittingConfigurationsHasFilter,
  selectActiveFittingConfigurations,
  selectConfigurationQuery,
  selectTappedHolesFilterSplit,
  selectOringFilterSplit,
  selectBevelFilterSplit,
  (hasFilter, configs, q, tappedHoles, orings, bevels) => !hasFilter ? [] : filterConfigurations(configs, q, tappedHoles, orings, bevels));

// Associated Products Configurations
export const selectAssociatedPartsConfigurationQuery = createSelector(state, state => state?.configurationFilterAssociatedParts);
export const selectActiveFittingAssociatedParts = createSelector(selectActiveFittingDetail, detail => detail?.associatedProducts || []);
export const selectActiveFittingSelectedAssociatedPart = createSelector(state, state => state.activeFittingSelectedAssociatedPart);
export const selectActiveFittingSelectedAssociatedPartDetail = createSelector(selectActiveFittingAssociatedParts, selectActiveFittingSelectedAssociatedPart,
  (parts, selected) => parts.find(f => f.partNumber === selected));
export const selectedActiveFittingSelectedAssociatedPartConfigurations = createSelector(selectActiveFittingSelectedAssociatedPartDetail, detail => detail?.configurations || []);

export const selectAssPartsTappedHolesFilterValue = createSelector(selectAssociatedPartsConfigurationQuery, q => q?.tappedBlind);
export const selectAssPartsTappedHolesFilterSplit = createSelector(selectAssociatedPartsConfigurationQuery, q => (q?.tappedBlind || '').split(',').filter(f => f.length > 0).map(v => v.trim()));

export const selectAssPartsOringFilterValue = createSelector(selectAssociatedPartsConfigurationQuery, q => q?.oring);
export const selectAssPartsOringFilterSplit = createSelector(selectAssociatedPartsConfigurationQuery, q => (q?.oring || '').split(',').filter(f => f.length > 0).map(v => v.trim()));

export const selectAssPartsBevelFilterValue = createSelector(selectAssociatedPartsConfigurationQuery, q => q?.bevel);
export const selectAssPartsBevelFilterSplit = createSelector(selectAssociatedPartsConfigurationQuery, q => (q?.bevel || '').split(',').filter(f => f.length > 0).map(v => v.trim()));

// we don't show anything for the associated product configs unless they first choose a filter. per Daniel Brownfield
export const selectAssociatedPartsActiveFittingConfigurationsHasFilter = createSelector(
  selectAssociatedPartsConfigurationQuery,
  (q) => !(!q?.buyAmerica && !q?.magInspection && !q?.liftingLugs &&
    (q?.tappedBlind || '').length === 0 && (q?.oring || '').length === 0 && (q?.bevel || '').length === 0));

export const selectAssociatedPartsActiveFittingConfigurationsWithFilter = createSelector(
  selectAssociatedPartsActiveFittingConfigurationsHasFilter,
  selectedActiveFittingSelectedAssociatedPartConfigurations,
  selectAssociatedPartsConfigurationQuery,
  selectAssPartsTappedHolesFilterSplit,
  selectAssPartsOringFilterSplit,
  selectAssPartsBevelFilterSplit,
  (hasFilter, configs, q, tappedHoles, orings, bevels) => !hasFilter ? [] : filterConfigurations(configs, q, tappedHoles, orings, bevels));

// if the design code is B31.3 (Process Piping) then clear the design codes and only allow an N/A option
// if the design code is EN-13480 then clear the design codes and only allow an N/A option and convert design temps to C and set min to -30 and max to 50
export const selectDesignFactorsWithDesignCodeOptionEffect = createSelector(selectDesignFactors, selectDesignCode, (designFactors, designCode) => {
  switch (designCode) {
    case 1: // B31.3 (Process Piping)
      return [{ ...designFactors.find(f => f.id === -1), text: 'N/A' }];
    case 6: // EN-13480
      return [{ ...designFactors.find(f => f.id === -1), text: 'N/A' }];
    default:
      return designFactors;
  }
});

export const selectLoaders = createSelector(state, state => state.loaders);
export const selectLoader = (name: fromReducer.Loaders) =>
  createSelector(selectLoaders, (loaders: fromReducer.Loaders[]) => !!((loaders || []).find(f => f === name)));
